import { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { arrivedTotals, type ArrivedTotals } from '@/lib/donation-arrivals';
import { WINDOW_REFETCH_MS } from '@/lib/live-window';

/** How often the clock is read while donations are still arriving. */
const TICK_MS = 50;

/**
 * How long a window's payload stays fresh. Shorter than the server's own 10-minute cache so the
 * interval below always finds the query stale enough to go out and pick up the next window.
 */
const STALE_MS = 60 * 1000;

/**
 * These entries are worth one window and nothing after it, so they are not kept under the router's
 * `gcTime: Infinity`. Three windows of headroom: long enough that a background tab returning to focus
 * still finds its data, short enough that nothing accumulates (TEC-3807).
 */
const GC_MS = 15 * 60 * 1000;

/**
 * The live figures behind the homepage bento: real money raised and a real donation count, both climbing
 * as the donations of 24 hours earlier are replayed at their own pace.
 *
 * ONE hook for BOTH figures, deliberately. The two counters sit in different tiles, but they must never
 * disagree — see `lib/donation-arrivals.ts`. Calling this twice would give them two clocks.
 *
 * THE QUERY KEY IS STABLE, and that is the whole reason the counters no longer vanish every five
 * minutes. The window used to be computed in the browser and sent as input, so the key changed 288 times
 * a day; a new key has no data, `useSuspenseQuery` reports `isPending`, and React showed the Suspense
 * fallback again. The server now derives the window itself (`lib/live-window.ts`), so the rollover is a
 * refetch of a key that already holds data — `status` stays `success`, nothing suspends, and the
 * boundary covers the first load only, which is what a boundary is for.
 *
 * The first render is deterministic: `now` starts at 0, so no donation has arrived yet and the server
 * and the browser both render exactly the baseline. The real clock starts after mount. Do not "improve"
 * that initial value — this application has a long history of React error 418 from precisely that.
 */
export function useLiveDonationTotals(): ArrivedTotals {
	const { data } = useSuspenseQuery(
		trpc.portal.getRecentDonations.queryOptions(
			{ mode: 'day' },
			{
				staleTime: STALE_MS,
				gcTime: GC_MS,
				refetchInterval: WINDOW_REFETCH_MS,
				// The counter is the point of the page it sits on; a visitor coming back to the tab should
				// find it current rather than frozen where they left it.
				refetchIntervalInBackground: false,
			},
		),
	);

	const [now, setNow] = useState(0);
	const donations = data.donations;

	useEffect(() => {
		setNow(Date.now());
		if (donations.length === 0) return;
		// Nothing left to wait for once the last timestamp has passed: the interval would then tick 20
		// times a second for the rest of the window, recomputing a total that cannot change.
		if (donations.every((donation) => donation.timestamp <= Date.now())) return;
		const timer = setInterval(() => setNow(Date.now()), TICK_MS);
		return () => clearInterval(timer);
	}, [donations]);

	return arrivedTotals(data, donations, now);
}
